import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Form,
    Input,
    InputNumber,
    Space,
    Switch,
    Tabs,
    Typography,
    message,
    Table,
    Modal
} from 'antd';
import { kedoGet, kedoPost, kedoPut } from './kedoApi';

const { Title, Paragraph, Text } = Typography;

const KedoSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [smsForm] = Form.useForm();
    const [goskeyForm] = Form.useForm();
    const [cryptoForm] = Form.useForm();
    const [cryptoproForm] = Form.useForm();
    const [clients, setClients] = useState([]);
    const [webhooks, setWebhooks] = useState([]);
    const [createdKey, setCreatedKey] = useState(null);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const [settings, apiClients, hooks] = await Promise.all([
                kedoGet('/settings'),
                kedoGet('/api-clients'),
                kedoGet('/webhooks')
            ]);
            smsForm.setFieldsValue(settings?.sms || {});
            goskeyForm.setFieldsValue(settings?.goskey || {});
            cryptoForm.setFieldsValue({
                enabled: !!settings?.encryption?.enabled,
                master_key_set: !!settings?.encryption?.master_key_set
            });
            cryptoproForm.setFieldsValue(settings?.cryptopro || {});
            setClients(apiClients || []);
            setWebhooks(hooks || []);
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e.message ||
                    'Нет доступа к настройкам (нужна роль owner/admin)'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveSection = async (section, values) => {
        try {
            await kedoPut('/settings', { [section]: values });
            message.success('Сохранено');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const createClient = async () => {
        try {
            const data = await kedoPost('/api-clients', {
                name: 'Integration'
            });
            setCreatedKey(data?.api_key);
            message.success('API-ключ создан');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    const createWebhook = async values => {
        try {
            await kedoPost('/webhooks', {
                url: values.url,
                events: (values.events || 'document.signed,route.completed')
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
            });
            message.success('Webhook добавлен');
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || e.message);
        }
    };

    if (error) {
        return <Alert type="error" message={error} showIcon />;
    }

    return (
        <>
            <Title level={2} style={{ fontFamily: 'Georgia, serif' }}>
                Настройки Kedo
            </Title>
            <Paragraph type="secondary">
                Провайдеры подписи, шифрование at-rest, API-ключи и webhooks
                с HMAC.
            </Paragraph>

            <div className="kedo-panel">
                <Tabs
                    items={[
                        {
                            key: 'sms',
                            label: 'SMS ПЭП',
                            children: (
                                <Form
                                    layout="vertical"
                                    form={smsForm}
                                    onFinish={v => saveSection('sms', v)}
                                    disabled={loading}
                                    style={{ maxWidth: 520 }}
                                >
                                    <Form.Item name="provider" label="Провайдер">
                                        <Input placeholder="stub | smsru | smsc" />
                                    </Form.Item>
                                    <Form.Item name="api_key" label="API key">
                                        <Input.Password />
                                    </Form.Item>
                                    <Form.Item name="sender" label="Имя отправителя">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        name="template"
                                        label="Шаблон сообщения"
                                    >
                                        <Input.TextArea
                                            rows={2}
                                            placeholder="Код подписания Kedo: {code}"
                                        />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Сохранить SMS
                                    </Button>
                                </Form>
                            )
                        },
                        {
                            key: 'goskey',
                            label: 'Госключ',
                            children: (
                                <Form
                                    layout="vertical"
                                    form={goskeyForm}
                                    onFinish={v => saveSection('goskey', v)}
                                    disabled={loading}
                                    style={{ maxWidth: 520 }}
                                >
                                    <Form.Item
                                        name="use_crm_goskey_module"
                                        label="Использовать CRM goskeyService"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item
                                        name="callback_url"
                                        label="Webhook URL (домен Kedo)"
                                    >
                                        <Input placeholder="https://edo.example/api/..." />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Сохранить Госключ
                                    </Button>
                                </Form>
                            )
                        },
                        {
                            key: 'cryptopro',
                            label: 'КриптоПро / DSS',
                            children: (
                                <Form
                                    layout="vertical"
                                    form={cryptoproForm}
                                    onFinish={v => saveSection('cryptopro', v)}
                                    disabled={loading}
                                    style={{ maxWidth: 520 }}
                                >
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 12 }}
                                        message="Этап 3: контракт провайдера. Сейчас сохраняются параметры и UI-заглушка."
                                    />
                                    <Form.Item name="dss_url" label="DSS URL">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="client_id" label="Client ID">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        name="enabled"
                                        label="Включено"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Сохранить КриптоПро
                                    </Button>
                                </Form>
                            )
                        },
                        {
                            key: 'encryption',
                            label: 'Шифрование',
                            children: (
                                <Form
                                    layout="vertical"
                                    form={cryptoForm}
                                    onFinish={v =>
                                        saveSection('encryption', {
                                            enabled: !!v.enabled,
                                            master_key: v.master_key || undefined
                                        })
                                    }
                                    disabled={loading}
                                    style={{ maxWidth: 520 }}
                                >
                                    <Form.Item
                                        name="enabled"
                                        label="AES-256-GCM at-rest"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item
                                        name="master_key"
                                        label="Master key (hex 64 или passphrase)"
                                        extra="Ключ хранится зашифрованно в настройках организации"
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                    <Form.Item
                                        name="master_key_set"
                                        label="Ключ задан"
                                        valuePropName="checked"
                                    >
                                        <Switch disabled />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Сохранить шифрование
                                    </Button>
                                </Form>
                            )
                        },
                        {
                            key: 'api',
                            label: 'API-ключи CRM',
                            children: (
                                <>
                                    <Space style={{ marginBottom: 12 }}>
                                        <Button type="primary" onClick={createClient}>
                                            Выпустить API-ключ
                                        </Button>
                                    </Space>
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        pagination={false}
                                        dataSource={clients}
                                        columns={[
                                            { title: 'ID', dataIndex: 'id', width: 60 },
                                            { title: 'Имя', dataIndex: 'name' },
                                            {
                                                title: 'Префикс',
                                                dataIndex: 'key_prefix'
                                            },
                                            {
                                                title: 'Активен',
                                                dataIndex: 'is_active',
                                                render: v => (v ? 'да' : 'нет')
                                            }
                                        ]}
                                    />
                                    <Paragraph style={{ marginTop: 12 }}>
                                        Заголовок:{' '}
                                        <Text code>X-Kedo-Api-Key: &lt;key&gt;</Text>
                                    </Paragraph>
                                </>
                            )
                        },
                        {
                            key: 'webhooks',
                            label: 'Webhooks',
                            children: (
                                <>
                                    <Form
                                        layout="inline"
                                        onFinish={createWebhook}
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Form.Item
                                            name="url"
                                            rules={[{ required: true }]}
                                        >
                                            <Input
                                                style={{ width: 320 }}
                                                placeholder="https://crm.../api/kedo-webhook"
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="events"
                                            initialValue="document.signed,route.completed"
                                        >
                                            <Input style={{ width: 260 }} />
                                        </Form.Item>
                                        <Button type="primary" htmlType="submit">
                                            Добавить
                                        </Button>
                                    </Form>
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        pagination={false}
                                        dataSource={webhooks}
                                        columns={[
                                            { title: 'URL', dataIndex: 'url' },
                                            {
                                                title: 'Events',
                                                dataIndex: 'events',
                                                render: e =>
                                                    Array.isArray(e)
                                                        ? e.join(', ')
                                                        : String(e || '')
                                            },
                                            {
                                                title: 'Secret',
                                                dataIndex: 'secret'
                                            }
                                        ]}
                                    />
                                </>
                            )
                        },
                        {
                            key: 'limits',
                            label: 'Rate limit',
                            children: (
                                <Form
                                    layout="vertical"
                                    initialValues={{
                                        sign_per_minute: 10,
                                        login_per_minute: 20
                                    }}
                                    onFinish={v => saveSection('rate_limit', v)}
                                    style={{ maxWidth: 360 }}
                                >
                                    <Form.Item
                                        name="sign_per_minute"
                                        label="Подписаний / мин"
                                    >
                                        <InputNumber min={1} max={120} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item
                                        name="login_per_minute"
                                        label="Auth попыток / мин"
                                    >
                                        <InputNumber min={1} max={200} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Сохранить лимиты
                                    </Button>
                                </Form>
                            )
                        }
                    ]}
                />
            </div>

            <Modal
                title="Сохраните API-ключ"
                open={!!createdKey}
                onOk={() => setCreatedKey(null)}
                onCancel={() => setCreatedKey(null)}
                okText="Готово"
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <Alert
                    type="warning"
                    showIcon
                    message="Ключ показывается один раз"
                    description={<Text copyable code>{createdKey}</Text>}
                />
            </Modal>
        </>
    );
};

export default KedoSettingsPage;
